import {
  ICustomLineItem,
  IInteractionV2,
  IInvoice,
  InvoiceType,
  IPatient,
  IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  IReffable,
  Timestamp,
  toNamedDocument,
  Transaction,
  WithRef,
} from '@principle-theorem/shared';
import { OrganisationCache } from '../organisation/organisation-cache';
import { Patient } from '../patient/patient';
import { toAccountDetails } from './account-details';
import { BillingEntity } from './billing-entity';
import { Invoice } from './invoice';
import { InvoiceInteractionBuilder } from './invoice-interaction-builder';
import { Practice } from '../practice/practice';

export interface ICreateInvoiceWriteOptions {
  transaction?: Transaction;
  invoiceRef?: DocumentReference<IInvoice>;
  interactionRef?: DocumentReference<IInteractionV2>;
  /**
   * Only the settlement timestamps are overridable. `Partial<IInvoice>` would
   * widen `status` across the discriminated union and break the invoice's own
   * type, and no caller needs to move an invoice between states this way.
   */
  invoiceOverrides?: { issuedAt?: Timestamp; paidAt?: Timestamp };
  /**
   * Fields stamped onto the "created invoice" interaction. Only callers that
   * hold context the builder cannot see (the deposit flow's practice) supply
   * this; omitting it leaves the interaction exactly as the builder produced it.
   */
  interactionOverrides?: Partial<IInteractionV2>;
}

export async function createInvoice(
  patient: IReffable<IPatient>,
  practice: WithRef<IPractice>,
  staffer?: WithRef<IStaffer>,
  items: ICustomLineItem[] = [],
  invoiceType: InvoiceType = InvoiceType.Invoice,
  writeOptions?: ICreateInvoiceWriteOptions
): Promise<WithRef<IInvoice>> {
  const resolvedPatient = await OrganisationCache.patients.getDoc(patient.ref);
  const primaryContact = await Patient.resolvePrimaryContact(resolvedPatient);

  const baseInvoice: IInvoice = Invoice.init({
    to: primaryContact
      ? toAccountDetails({ patient: resolvedPatient, primaryContact })
      : toAccountDetails(resolvedPatient),
    from: toAccountDetails(practice),
    practice: toNamedDocument(practice),
    items,
    type: invoiceType,
    includeDepositsInAllocations:
      practice.settings?.accounting?.includeDepositsInAllocations ?? false,
  });

  const brand = await OrganisationCache.brands.getDoc(
    Practice.brandRef(practice)
  );
  const billingEntity = brand.settings.billing?.enableBillingEntitySelection
    ? await BillingEntity.getBillingEntityForInvoice(baseInvoice)
    : undefined;

  const invoice: IInvoice = {
    ...baseInvoice,
    ...(billingEntity
      ? {
          from: billingEntity.from,
          attributedTo: billingEntity.attributedTo,
        }
      : {}),
    ...writeOptions?.invoiceOverrides,
  };

  const ref = await Firestore.addDoc(
    Patient.invoiceCol(patient),
    invoice,
    writeOptions?.invoiceRef,
    writeOptions?.transaction
  );

  const result = { ...invoice, ref };
  await Invoice.addInteraction(
    result,
    {
      ...InvoiceInteractionBuilder.createdInteraction(staffer),
      ...writeOptions?.interactionOverrides,
    },
    writeOptions?.transaction,
    writeOptions?.interactionRef
  );
  return result;
}
